import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, IconButton, Avatar, Chip, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { Icon } from '@iconify/react';
import { common, grey } from 'src/theme/palette';
import { piiProps } from 'src/components/DemoBlur';


const STATUS_CONFIG = {
    NEW: { color: '#00A76F', contactLabel: 'Contact Lead', followUpLabel: 'Follow up' },
    CONSULTATION_BOOKED: { color: '#1890FF', contactLabel: 'Record Attendance' },
    CONSULTATION_ATTENDED: { color: '#54D62C', contactLabel: 'Follow up' },
    TREATMENT_STARTED: { color: '#FFC107', contactLabel: 'Complete Treatment' },
    FAILED_TO_ATTEND_CONSULTATION: { color: '#FF4842', contactLabel: 'Follow up' },
    CLOSED: { color: '#637381', contactLabel: 'View' },
    DEFAULT: { color: grey[300], contactLabel: 'Contact Lead' },
};

const getStatusConfig = (status, contactedCount) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.DEFAULT;
    if (status === 'NEW' && contactedCount > 0) {
        return { ...config, contactLabel: config.followUpLabel || 'Follow up' };
    }
    return config;
};

// Format a raw referral string (e.g. "GOOGLE_ADS", "FRIEND") into a
// human-readable label without needing the Redux referral list.
const formatReferral = (value) => {
    if (!value) return null;
    return value
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
};

const getRelativeTime = (dateStr) => {
    if (!dateStr) return 'No contact';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
};

const MobileLeadCard = ({ row, onContact, onMoreAction, onViewDetails }) => {
    const [menuAnchor, setMenuAnchor] = React.useState(null);

    const {
        /* eslint-disable camelcase */
        first_name: firstName,
        last_name: lastName,
        email,
        phone,
        treatment,
        treatment_colour: treatmentColour,
        status,
        contacted_count: contactedCount,
        last_contacted_date: lastContactedDate,
        overdue_task_count: overdueTaskCount,
        assignees: rawAssignees,
        referral,
        /* eslint-enable camelcase */
    } = row;

    // Role-guarded direct map (mirrors AssigneeField display at AssigneeField.jsx:23-24
    // which reads assignees[].user.first_name/last_name directly without is_enabled).
    // FilterAssigneeList's is_enabled gate is picker-specific and 400s on this endpoint
    // because the leads field-spec doesn't expose is_enabled on the assignees subset.
    // We preserve the documented security boundary (CLAUDE.md) by role-filtering inline.
    const safeAssignees = React.useMemo(
        () => (rawAssignees || [])
            .filter((el) => el?.role !== 'ADMIN' && el?.role !== 'SUPPORT' && !el?.user?.is_superuser)
            .map((el) => ({
                name: `${el?.user?.first_name || ''} ${el?.user?.last_name || ''}`.trim(),
                avatar: el?.user?.avatar,
                role: el?.role,
            }))
            .filter((a) => a.name),
        [rawAssignees],
    );
    const referralLabel = formatReferral(referral);

    const fullName = `${firstName || ''} ${lastName || ''}`.trim() || 'Unknown';
    const initials = `${(firstName || '?')[0]}${(lastName || '?')[0]}`.toUpperCase();
    const statusConfig = getStatusConfig(status, contactedCount);

    const handleViewDetails = () => {
        if (onViewDetails) onViewDetails(row);
    };

    const handleContact = () => {
        if (onContact) onContact(row);
    };

    const handleMenuOpen = (e) => {
        e.stopPropagation();
        setMenuAnchor(e.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleMenuAction = (action) => {
        handleMenuClose();
        if (onMoreAction) onMoreAction(row, action);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                p: '12px 14px',
                borderLeft: `3px solid ${statusConfig.color}`,
                borderBottom: '1px solid rgba(145, 158, 171, 0.12)',
                bgcolor: '#fff',
                '&:active': { bgcolor: 'rgba(145, 158, 171, 0.08)' },
            }}
        >
            {/* Row 1: Avatar, Name, Phone/Email icons, Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Avatar
                    {...piiProps}
                    sx={{
                        width: 36, height: 36, fontSize: '13px', fontWeight: 600,
                        bgcolor: statusConfig.color, color: common.white, flexShrink: 0,
                    }}
                >
                    {initials}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        {...piiProps}
                        sx={{
                            fontSize: '13px', fontWeight: 600, color: '#212B36',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                    >
                        {fullName}
                    </Typography>
                </Box>
                {phone && (
                    <IconButton
                        component="a"
                        href={`tel:${phone}`}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ width: 44, height: 44, color: '#637381' }}
                    >
                        <Icon icon="mdi:phone-outline" width={18} />
                    </IconButton>
                )}
                {email && (
                    <IconButton
                        component="a"
                        href={`mailto:${email}`}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ width: 44, height: 44, color: '#637381' }}
                    >
                        <Icon icon="mdi:email-outline" width={18} />
                    </IconButton>
                )}
                <IconButton onClick={handleMenuOpen} sx={{ width: 44, height: 44, color: '#637381' }}>
                    <Icon icon="mdi:dots-vertical" width={20} />
                </IconButton>
                <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
                    <MenuItem onClick={() => handleMenuAction('archive')}>
                        <ListItemIcon><Icon icon="mdi:archive-outline" width={18} /></ListItemIcon>
                        <ListItemText>Archive</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleMenuAction('edit')}>
                        <ListItemIcon><Icon icon="mdi:pencil-outline" width={18} /></ListItemIcon>
                        <ListItemText>Edit</ListItemText>
                    </MenuItem>
                </Menu>
            </Box>

            {/* Row 2: Treatment */}
            {treatment && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', pl: '46px' }}>
                    {treatmentColour && (
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: treatmentColour, flexShrink: 0 }} />
                    )}
                    <Typography sx={{ fontSize: '12px', color: '#637381' }}>
                        {treatment}
                    </Typography>
                </Box>
            )}

            {/* Row 3: Status + Last contacted + task-due indicator */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', pl: '46px' }}>
                {contactedCount > 0 && (
                    <Chip
                        label={`Follow up ${contactedCount}`}
                        size="small"
                        sx={{
                            height: '22px', fontSize: '11px', fontWeight: 600,
                            bgcolor: 'rgba(0, 167, 111, 0.08)', color: '#007B55',
                        }}
                    />
                )}
                {/* Overdue task indicator — mirrors Follow-up chip above (lines 165-172).
                    Field verified: overdue_task_count mapped at useLeadTableHelper.js:186
                    from item?.overdue_task_count ?? 0. Only shown when count > 0. */}
                {overdueTaskCount > 0 && (
                    <Chip
                        label={`Overdue ${overdueTaskCount}`}
                        size="small"
                        sx={{
                            height: '22px', fontSize: '11px', fontWeight: 600,
                            bgcolor: 'rgba(255, 86, 48, 0.08)', color: '#B71D18',
                        }}
                    />
                )}
                <Typography sx={{ fontSize: '12px', color: '#919EAB' }}>
                    {getRelativeTime(lastContactedDate)}
                </Typography>
            </Box>

            {/* Row 3.5: Assignee + referral source — only rendered when present.
                Fields verified: assignees at useLeadTableHelper.js:125,
                referral at useLeadTableHelper.js:169. Assignees are filtered
                through FilterAssigneeList (security boundary — strips ADMIN /
                SUPPORT / superuser). Referral formatted locally without Redux. */}
            {(safeAssignees.length > 0 || referralLabel) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', pl: '46px', flexWrap: 'wrap' }}>
                    {safeAssignees.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Icon icon="mdi:account-outline" width={13} style={{ color: '#919EAB', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '11px', color: '#637381', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {safeAssignees.length === 1
                                    ? safeAssignees[0].name
                                    : `${safeAssignees[0].name} +${safeAssignees.length - 1}`}
                            </Typography>
                        </Box>
                    )}
                    {referralLabel && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Icon icon="mdi:tag-outline" width={13} style={{ color: '#919EAB', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '11px', color: '#637381', maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {referralLabel}
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* Row 4: Action buttons */}
            <Box sx={{ display: 'flex', gap: '8px', pl: '46px', mt: '4px' }}>
                <Box
                    component="button"
                    onClick={handleContact}
                    sx={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        gap: '4px', py: '6px', px: '8px',
                        border: '1px solid', borderColor: statusConfig.color, borderRadius: '8px',
                        bgcolor: 'transparent', color: statusConfig.color,
                        fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.15s',
                        '&:active': { bgcolor: `${statusConfig.color}14` },
                    }}
                >
                    <Icon icon="mdi:phone-outline" width={14} />
                    {statusConfig.contactLabel}
                </Box>
                <Box
                    component="button"
                    onClick={handleViewDetails}
                    sx={{
                        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        gap: '4px', py: '6px', px: '8px',
                        border: '1px solid rgba(145, 158, 171, 0.24)', borderRadius: '8px',
                        bgcolor: 'transparent', color: '#212B36',
                        fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.15s',
                        '&:active': { bgcolor: 'rgba(145, 158, 171, 0.08)' },
                    }}
                >
                    Details
                    <Icon icon="mdi:chevron-right" width={14} />
                </Box>
            </Box>
        </Box>
    );
};

MobileLeadCard.propTypes = {
    row: PropTypes.object.isRequired,
    onContact: PropTypes.func,
    onMoreAction: PropTypes.func,
    onViewDetails: PropTypes.func,
};

export default MobileLeadCard;
